import { Alert, AlertDescription, AlertIcon, Box, Flex, Text, Spinner, useColorModeValue } from '@chakra-ui/react';
import { MdCheckCircle, MdError, MdRadioButtonUnchecked } from 'react-icons/md';
import { CourtRecordingDto, CourtRecordingStatus } from '../../../models/court-recording/dto/court-recording.dto';
import { translate, TranslationKeys } from '../../../i18n';

const PIPELINE_STAGES = [
  'downloading',
  'video_metadata',
  'tracking',
  'detecting_court',
  'heatmaps',
  'events',
  'statistics',
] as const;

function getStageLabel(stage: string): string {
  const key = `court_recording.stage_${stage}` as TranslationKeys;
  return translate(key) ?? stage;
}

type StageStatus = 'completed' | 'in_progress' | 'pending' | 'failed';

function getStageStatus(
  stageName: string,
  recording: CourtRecordingDto,
): StageStatus {
  const stages = recording.stages ?? [];
  const stageData = stages.find((s) => s.stage === stageName);

  if (!stageData) return 'pending';
  if (stageData.error) return 'failed';
  if (stageData.completedAt) return 'completed';
  return 'in_progress';
}

function getStageTimestamp(stageName: string, recording: CourtRecordingDto): string | undefined {
  const stageData = recording.stages?.find((s) => s.stage === stageName);
  if (!stageData) return undefined;
  const ts = stageData.completedAt ?? stageData.startedAt;
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function getProgressSummary(recording: CourtRecordingDto): string | null {
  if (
    recording.status !== CourtRecordingStatus.PROCESSING &&
    recording.status !== CourtRecordingStatus.QUEUED
  ) {
    return null;
  }

  if (!recording.currentStage) return null;

  const currentIdx = PIPELINE_STAGES.indexOf(recording.currentStage as typeof PIPELINE_STAGES[number]);
  if (currentIdx === -1) return null;

  const label = getStageLabel(recording.currentStage);
  return translate('court_recording.processing_step')
    .replace('{current}', String(currentIdx + 1))
    .replace('{total}', String(PIPELINE_STAGES.length))
    .replace('{label}', label);
}

export default function ProcessingTimeline({ recording }: { recording: CourtRecordingDto }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('secondaryGray.600', 'whiteAlpha.600');
  const lineColor = useColorModeValue('gray.200', 'whiteAlpha.300');

  return (
    <Box>
      <Text fontSize="md" fontWeight="600" color={textColor} mb={4}>
        {translate('court_recording.processing_timeline')}
      </Text>

      {recording.status === CourtRecordingStatus.QUEUED && recording.lastTriggerError && (
        <Alert status="warning" variant="left-accent" mb={4} fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            {translate('court_recording.trigger_error').replace('{error}', recording.lastTriggerError)}
            <Text fontSize="xs" color="gray.500" mt={1}>
              {translate('court_recording.auto_retry_note')}
            </Text>
          </AlertDescription>
        </Alert>
      )}

      {recording.status === CourtRecordingStatus.QUEUED && !recording.lastTriggerError && (
        <Alert status="info" variant="left-accent" mb={4} fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            {translate('court_recording.waiting_for_slot')}
          </AlertDescription>
        </Alert>
      )}

      {PIPELINE_STAGES.map((stage, idx) => {
        const status = getStageStatus(stage, recording);
        const timestamp = getStageTimestamp(stage, recording);
        const isLast = idx === PIPELINE_STAGES.length - 1;

        return (
          <Flex key={stage} align="flex-start">
            {/* Icon + connector line */}
            <Flex direction="column" align="center" mr={3} minW="24px">
              <StageIcon status={status} />
              {!isLast && (
                <Box w="2px" bg={lineColor} flex="1" minH="24px" />
              )}
            </Flex>

            {/* Label + timestamp */}
            <Box pb={isLast ? 0 : 4} pt="2px">
              <Text
                fontSize="sm"
                fontWeight={status === 'in_progress' ? '600' : '400'}
                color={status === 'pending' ? mutedColor : status === 'failed' ? 'red.400' : textColor}
              >
                {getStageLabel(stage)}
              </Text>
              {timestamp && (
                <Text fontSize="xs" color={mutedColor}>
                  {timestamp}
                </Text>
              )}
              {status === 'failed' && (
                <Text fontSize="xs" color="red.400">
                  {recording.stages?.find((s) => s.stage === stage)?.error}
                </Text>
              )}
            </Box>
          </Flex>
        );
      })}
    </Box>
  );
}

function StageIcon({ status }: { status: StageStatus }) {
  switch (status) {
    case 'completed':
      return <Box as={MdCheckCircle} color="green.400" fontSize="20px" />;
    case 'in_progress':
      return <Spinner size="sm" color="brand.400" thickness="3px" />;
    case 'failed':
      return <Box as={MdError} color="red.400" fontSize="20px" />;
    case 'pending':
      return <Box as={MdRadioButtonUnchecked} color="gray.300" fontSize="20px" />;
  }
}
